import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useGroupsListQuery } from '~/queries/user-groups'
import {
  useAddGroupsToUserMutation,
  useAddRolesToUserMutation,
  useCreateUsersMutation,
  keys as usersManagementKeys,
  useUpdateUserEmailMutation,
  useUpdateUserNameMutation,
  keys as mwmUsersKeys,
} from '~/queries/user-management'
import { useRolesListQuery } from '~/queries/user-roles'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

import './styles.scss'

const MODAL_ID = 'manage-user-modal'

export interface FormValues {
  email: string
  groups: IOption[]
  name: string
  roles: IOption[]
}

const validationSchema = Yup.object().shape({
  email: Yup.string().trim().required(FORM_ERRORS.REQUIRED).email(FORM_ERRORS.INVALID_EMAIL),
  groups: Yup.array().of(RequiredYupOption).required(),
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  roles: Yup.array().of(RequiredYupOption).required(),
})

interface FormProps {
  email?: string
  name?: string
  onClose: VoidFunction
  onFailure: (error: unknown) => void
  onSuccess: VoidFunction
  userId?: string
}

const ManageUserForm = ({ email, name, onClose, onFailure, onSuccess, userId }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const defaultValues = {
    email: email || '',
    groups: [],
    name: name || '',
    roles: [],
  }

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setError,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { data: groups, isLoading: groupsLoading } = useGroupsListQuery()

  const { data: roles, isLoading: rolesLoading } = useRolesListQuery()

  const { mutateAsync: createUser } = useCreateUsersMutation()

  const { mutateAsync: updateUserEmail } = useUpdateUserEmailMutation()

  const { mutateAsync: updateUserName } = useUpdateUserNameMutation()

  const { mutateAsync: addGroupsMutation } = useAddGroupsToUserMutation()

  const { mutateAsync: addRolesMutation } = useAddRolesToUserMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      if (userId) {
        if (values.email !== email) await updateUserEmail({ email: values.email.trim(), userId })
        if (values.name !== name) await updateUserName({ name: values.name.trim(), userId })
        await queryClient.invalidateQueries({ queryKey: mwmUsersKeys.details(userId) })
      } else {
        const [newCreatedUser] = await createUser([{ email: values.email.trim(), name: values.name.trim() }])

        if (values.groups.length && newCreatedUser) {
          const groupIds = values.groups.map(({ value }) => value)
          await addGroupsMutation({ groupIds, userId: newCreatedUser.user_id })
        }

        if (values.roles.length && newCreatedUser) {
          const roleIds = values.roles.map(({ value }) => value)
          await addRolesMutation({ roleIds, userId: newCreatedUser.user_id })
        }
      }
      await queryClient.invalidateQueries({ queryKey: usersManagementKeys.list() })
      onSuccess()
    } catch (e) {
      if (isAxiosError(e)) {
        if (e.response?.data.code === 409) {
          setError('email', { message: 'This email is already used!' })
        } else {
          onFailure(e)
        }
      } else {
        onFailure(e)
      }
    }
  }

  const groupsOptions = groups?.data.map(({ group_name, id }) => ({ label: group_name, value: id })) || []

  const rolesOptions = roles?.data.map(({ name, role_id }) => ({ label: name, value: role_id })) || []

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <TextInput
            {...register('name')}
            autoFocus
            error={errors.name?.message}
            label="Full name"
            placeholder="Enter full name"
            required
          />
          <TextInput
            {...register('email')}
            error={errors.email?.message}
            label="Email"
            placeholder="Enter email"
            required
          />
          {!userId ? (
            <>
              <Controller
                control={control}
                name="groups"
                render={({ field }) => (
                  <MultiSelectInput
                    {...field}
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    error={errors.groups?.message}
                    isLoading={groupsLoading}
                    label="Groups"
                    options={groupsOptions}
                    placeholder="Select groups"
                  />
                )}
              />
              <Controller
                control={control}
                name="roles"
                render={({ field }) => (
                  <MultiSelectInput
                    {...field}
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    error={errors.roles?.message}
                    isLoading={rolesLoading}
                    label="Specific roles"
                    options={rolesOptions}
                    placeholder="Select roles"
                  />
                )}
              />
            </>
          ) : null}
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageUserModal = ({ isOpen, onClose, userId, ...props }: ModalProps): ReactElement => {
  return (
    <Modal
      className="manage-user-modal"
      id={MODAL_ID}
      isOpen={isOpen}
      onCloseModal={onClose}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader>{userId ? 'Update user information' : 'Create a new user'}</ModalHeader>
      <ManageUserForm {...props} onClose={onClose} userId={userId} />
    </Modal>
  )
}

export default ManageUserModal
